import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BlogHeader } from '@/components/BlogHeader';
import { CTABanner } from '@/components/CTABanner';
import { PostHero } from '@/components/PostHero';
import { ReadingProgress } from '@/components/ReadingProgress';
import { RelatedPosts } from '@/components/RelatedPosts';
import { SocialShare } from '@/components/SocialShare';
import { InlineCTA } from '@/components/InlineCTA';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useKopfaiClickTracking } from '@/hooks/useKopfaiClickTracking';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { ArrowLeft, Calendar, Eye, Clock, Home } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

interface Post {
  id: number;
  title: string;
  slug: string;
  meta_description: string;
  keywords: string[];
  category: string;
  content: string;
  created_at: string;
  views: number;
  cover_image_url?: string;
}

export default function Post() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const { trackClick } = useKopfaiClickTracking();

  useEffect(() => {
    if (slug) {
      loadPost(slug);
    }
  }, [slug]);

  // Track clicks on KopfAI content links
  useEffect(() => {
    if (!post) return;

    const handleKopfaiLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('kopfai-content-link')) {
        trackClick('article_content_link', post.id, 'KopfAI');
      }
    };

    document.addEventListener('click', handleKopfaiLinkClick);

    return () => {
      document.removeEventListener('click', handleKopfaiLinkClick);
    };
  }, [post, trackClick]);

  const loadPost = async (postSlug: string) => {
    try {
      setLoading(true);
      
      // Buscar post
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', postSlug)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast.error('Post não encontrado');
        return;
      }

      setPost(data);

      // Incrementar views
      await supabase
        .from('posts')
        .update({ views: data.views + 1 })
        .eq('id', data.id);

    } catch (error) {
      console.error('Erro ao carregar post:', error);
      toast.error('Erro ao carregar post');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <main className="container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <main className="container mx-auto px-4 py-8 max-w-4xl text-center">
          <h1 className="text-2xl font-bold text-kopf-primary mb-4">Post não encontrado</h1>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Blog
            </Link>
          </Button>
        </main>
      </div>
    );
  }

  const calculateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const readingTime = calculateReadingTime(post.content);

  return (
    <>
      <Helmet>
        <title>{post.title} | KopfAI Blog</title>
        <meta name="description" content={post.meta_description} />
        <meta name="keywords" content={post.keywords.join(', ')} />
      </Helmet>

      <ReadingProgress />

      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <PostHero 
          title={post.title} 
          category={post.category}
          coverImageUrl={post.cover_image_url}
        />
        
        <main className="container mx-auto px-4 max-w-4xl">
          <Breadcrumb className="mb-4 sm:mb-6">
            <BreadcrumbList className="flex-wrap">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="flex items-center gap-1">
                    <Home className="w-4 h-4" />
                    <span className="hidden sm:inline">Início</span>
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="hidden sm:flex">
                <BreadcrumbLink className="text-kopf-text/60">
                  {post.category}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden sm:flex" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-kopf-primary line-clamp-1 max-w-[200px] sm:max-w-none text-sm sm:text-base">
                  {post.title}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <article>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-8 border-b border-kopf-secondary">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-kopf-text/60">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(post.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{readingTime} min de leitura</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{post.views} {post.views === 1 ? 'visualização' : 'visualizações'}</span>
                </div>
              </div>
              
              <SocialShare 
                url={`/post/${post.slug}`}
                title={post.title}
                description={post.meta_description}
              />
            </div>

            <div 
              className="prose-kopf"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <InlineCTA variant="default" postId={post.id} />

            <div className="mt-12 pt-8 border-t border-kopf-secondary">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-kopf-text/60 mb-3">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.keywords.map((keyword) => (
                      <Badge key={keyword} variant="outline" className="border-kopf-secondary text-kopf-text">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
                <SocialShare 
                  url={`/post/${post.slug}`}
                  title={post.title}
                  description={post.meta_description}
                />
              </div>
            </div>

            <RelatedPosts currentPostId={post.id} category={post.category} />
          </article>
        </main>

        <CTABanner />
      </div>
    </>
  );
}